import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Building2, Plus, Search, X, Eye, MapPin, Map, List, MoreHorizontal } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CreateLeadSheet } from "./CreateLeadSheet";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { LoadingSpinner } from "@/components/loading-spinner";
import { ProtectByPermission, PermissionButton } from "@/components/auth";
import { formatLeadSource, LEAD_SOURCES } from "@/lib/leadSources";
import { formatAccountType } from "@/lib/accountTypes";
import { geocodeAddress } from "@/lib/map-utils";
import { EmptyState } from "@/components/ui/empty-state";
import { UserPlus } from "lucide-react";
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  InfoWindow,
  useMap,
} from "@vis.gl/react-google-maps";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

interface Lead {
  _id: string;
  name: string;
  address?: string;
  accountType?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactTitle?: string;
  estimatedValue?: number;
  status: "new" | "contacted" | "qualified" | "unqualified";
  source?: string;
  coordinates?: { lat: number; lng: number };
}

function MapController({
  leads,
  center,
}: {
  leads: any[];
  center: { lat: number; lng: number };
}) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const withValidCoords = leads.filter(
      (l) =>
        l.coordinates &&
        typeof l.coordinates.lat === "number" &&
        typeof l.coordinates.lng === "number" &&
        !isNaN(l.coordinates.lat) &&
        !isNaN(l.coordinates.lng)
    );

    if (withValidCoords.length === 0) {
      map.panTo(center);
      map.setZoom(4);
      return;
    }

    if (withValidCoords.length === 1) {
      map.panTo({
        lat: withValidCoords[0].coordinates.lat,
        lng: withValidCoords[0].coordinates.lng,
      });
      map.setZoom(14);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    withValidCoords.forEach((l) => {
      bounds.extend({ lat: l.coordinates.lat, lng: l.coordinates.lng });
    });
    map.fitBounds(bounds, 60);
  }, [map, leads, center]);

  return null;
}

export default function LeadList() {
  const navigate = useNavigate();
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);

  // Fetch leads from Convex
  const leads = useQuery(api.businesses.getLeads) || [];
  const createLead = useMutation(api.businesses.create);
  const updateCoordinates = useMutation(api.businesses.updateCoordinates);

  // Auto-geocode leads missing coordinates when map view is active
  useEffect(() => {
    if (viewMode !== "map" || !Array.isArray(leads)) return;
    const missingCoords = leads.filter(
      (l: any) =>
        (!l.coordinates ||
          typeof l.coordinates.lat !== "number" ||
          typeof l.coordinates.lng !== "number" ||
          isNaN(l.coordinates.lat)) &&
        l.address &&
        l.address.trim().length > 0
    );
    if (missingCoords.length === 0) return;

    missingCoords.forEach(async (lead: any) => {
      try {
        const coords = await geocodeAddress(lead.address);
        if (coords) {
          await updateCoordinates({ id: lead._id, coordinates: coords });
        }
      } catch (err) {
        console.warn("Auto-geocoding failed for lead:", lead.name, err);
      }
    });
  }, [viewMode, leads, updateCoordinates]);

  const handleOpenCreateSheet = () => {
    setIsCreateSheetOpen(true);
  };

  const handleCloseCreateSheet = () => {
    setIsCreateSheetOpen(false);
  };

  const handleCreateLead = async (newLead: any) => {
    try {
      await createLead({
        name: newLead.name,
        address: newLead.address,
        coordinates: newLead.coordinates,
        accountType: newLead.accountType,
        source: newLead.source,
        estimatedValue: newLead.estimatedValue ? parseFloat(newLead.estimatedValue) : undefined,
        website: newLead.website,
        status: newLead.status || "new",
        contactName: newLead.contactName,
        contactEmail: newLead.contactEmail,
        contactPhone: newLead.contactPhone,
        contactTitle: newLead.contactTitle,
        // Opportunity fields
        opportunityDescription: newLead.opportunityDescription,
        budgetMin: newLead.budgetMin,
        budgetMax: newLead.budgetMax,
        employeeCount: newLead.employeeCount,
        frequency: newLead.frequency,
        potentialStartDate: newLead.potentialStartDate,
        estimatedSquareFootage: newLead.estimatedSquareFootage,
        requirements: newLead.requirements,
      });
      setIsCreateSheetOpen(false);
    } catch (error) {
      console.error("Error creating lead:", error);
    }
  };

  // No longer need to dynamically calculate sources - using predefined LEAD_SOURCES

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== null || sourceFilter !== null;

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter(null);
    setSourceFilter(null);
  };

  // Filter leads based on search query, status, and source
  const filteredLeads = Array.isArray(leads)
    ? leads.filter((lead) => {
        // Apply search filter
        const matchesSearch =
          searchQuery === "" ||
          lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (lead.contactName?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
          (lead.email?.toLowerCase() || "").includes(searchQuery.toLowerCase());

        // Apply status filter
        const matchesStatus = statusFilter === null || lead.status === statusFilter;

        // Apply source filter
        const matchesSource = sourceFilter === null || lead.source === sourceFilter;

        return matchesSearch && matchesStatus && matchesSource;
      })
    : [];

  // Calculate map data for leads with coordinates
  const { mapCenter, leadsWithCoords } = useMemo(() => {
    const withCoords = filteredLeads.filter(
      (l: any) =>
        l.coordinates &&
        typeof l.coordinates.lat === "number" &&
        typeof l.coordinates.lng === "number" &&
        !isNaN(l.coordinates.lat) &&
        !isNaN(l.coordinates.lng)
    );
    if (withCoords.length === 0) {
      return {
        mapCenter: { lat: 39.8283, lng: -98.5795 }, // Center of USA
        leadsWithCoords: [],
      };
    }
    const center = {
      lat: withCoords.reduce((sum: number, l: any) => sum + l.coordinates!.lat, 0) / withCoords.length,
      lng: withCoords.reduce((sum: number, l: any) => sum + l.coordinates!.lng, 0) / withCoords.length,
    };
    return { mapCenter: center, leadsWithCoords: withCoords };
  }, [filteredLeads]);

  // Show loading state while fetching data
  if (leads === undefined || leads === null) {
    return (
      <DashboardLayout activeTab="leads">
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ""}>
      <DashboardLayout activeTab="leads">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
              <p className="mt-1 text-gray-500">Manage your prospective clients</p>
            </div>
            <div className="flex gap-3">
              <ProtectByPermission permission="leads:write">
                <Button className="gap-2" onClick={handleOpenCreateSheet}>
                  <Plus className="h-4 w-4" /> Add Lead
                </Button>
              </ProtectByPermission>
            </div>
          </div>

          <Card className="w-full p-6">
            <div className="mb-6 flex flex-col gap-4">
              {/* Search and filters row */}
              <div className="flex flex-col gap-4 md:flex-row">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <Input
                    placeholder="Search leads..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select
                  value={statusFilter || "all"}
                  onValueChange={(value) => setStatusFilter(value === "all" ? null : value)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="unqualified">Unqualified</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={sourceFilter || "all"}
                  onValueChange={(value) => setSourceFilter(value === "all" ? null : value)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {LEAD_SOURCES.map((source) => (
                      <SelectItem key={source.value} value={source.value}>
                        {source.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="gap-2 text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-4 w-4" /> Clear Filters
                  </Button>
                )}
                <ToggleGroup
                  type="single"
                  value={viewMode}
                  onValueChange={(value: ViewMode) => value && setViewMode(value)}
                >
                  <ToggleGroupItem value="list" aria-label="List view">
                    <List className="mr-2 h-4 w-4" />
                    List
                  </ToggleGroupItem>
                  <ToggleGroupItem value="map" aria-label="Map view">
                    <Map className="mr-2 h-4 w-4" />
                    Map
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>

            {viewMode === "list" ? (
              <div className="w-full overflow-auto">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Account Type</TableHead>
                      <TableHead>Estimated Value</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <EmptyState
                            icon={UserPlus}
                            title="No leads yet"
                            description="Add your first lead to get started."
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLeads.map((lead) => (
                        <TableRow key={lead._id}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-gray-500" />
                                <span className="font-medium">{lead.name}</span>
                              </div>
                              <div className="pl-6 text-xs text-gray-500">
                                {lead.address || "No address provided"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{formatAccountType(lead.accountType)}</span>
                          </TableCell>
                          <TableCell>
                            {lead.estimatedValue ? (
                              <span className="font-medium">
                                ${lead.estimatedValue.toLocaleString()}
                              </span>
                            ) : (
                              <span className="text-gray-500">Not specified</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`capitalize ${
                                (
                                  {
                                    new: "bg-blue-100 text-blue-700",
                                    contacted: "bg-yellow-100 text-yellow-700",
                                    qualified: "bg-green-100 text-green-700",
                                    unqualified: "bg-red-100 text-red-700",
                                  } as Record<string, string>
                                )[lead.status] || "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {lead.status?.charAt(0).toUpperCase() + lead.status?.slice(1) ||
                                "New"}
                            </span>
                          </TableCell>
                          <TableCell>{formatLeadSource(lead.source)}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/leads/${lead._id}`)}>
                              <Eye className="mr-2 h-4 w-4" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="relative h-[650px] w-full overflow-hidden rounded-md border border-gray-200">
                {(() => {
                  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

                  if (!apiKey) {
                    return (
                      <div className="flex h-full w-full flex-col items-center justify-center bg-gray-50 p-4 text-center">
                        <MapPin className="mb-2 h-8 w-8 text-amber-600" />
                        <h3 className="text-lg font-medium text-gray-900">API Key Missing</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Google Maps API key is not configured. Map view is unavailable.
                        </p>
                      </div>
                    );
                  }

                  if (leadsWithCoords.length === 0) {
                    return (
                      <div className="flex h-full w-full flex-col items-center justify-center bg-gray-50 p-6 text-center">
                        <div className="mb-3 rounded-full bg-blue-50 p-3 text-blue-600">
                          <MapPin className="h-8 w-8" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">No Location Coordinates Available</h3>
                        <p className="mt-1 max-w-sm text-sm text-gray-500">
                          {filteredLeads.length > 0
                            ? `Found ${filteredLeads.length} leads, but none currently have map coordinates. Addresses will be automatically geocoded when available.`
                            : "No leads found matching the current filters."}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <>
                      {/* Floating Leads Count Badge */}
                      <div className="absolute left-3 top-3 z-10 flex items-center gap-2 rounded-full border border-gray-200/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-gray-800 shadow-md backdrop-blur-sm">
                        <MapPin className="h-3.5 w-3.5 text-blue-600" />
                        <span>
                          {leadsWithCoords.length} {leadsWithCoords.length === 1 ? "lead" : "leads"} mapped
                        </span>
                        {filteredLeads.length > leadsWithCoords.length && (
                          <span className="text-amber-600">
                            ({filteredLeads.length - leadsWithCoords.length} missing coordinates)
                          </span>
                        )}
                      </div>

                      <GoogleMap
                        defaultCenter={mapCenter}
                        defaultZoom={11}
                        gestureHandling="greedy"
                        disableDefaultUI={false}
                        mapId="LEADS_MAP"
                      >
                        <MapController leads={leadsWithCoords} center={mapCenter} />

                        {leadsWithCoords.map((lead: any) => (
                          <AdvancedMarker
                            key={lead._id}
                            position={{ lat: lead.coordinates.lat, lng: lead.coordinates.lng }}
                            onClick={() => setSelectedMarker(lead._id)}
                          >
                            <div
                              className={`relative flex transform cursor-pointer items-center justify-center transition-transform duration-200 ${
                                selectedMarker === lead._id
                                  ? "z-50 scale-125"
                                  : "z-10 scale-100 hover:scale-110"
                              }`}
                            >
                              <div
                                className={`absolute inset-0 rounded-full opacity-40 blur-[2px] ${
                                  (
                                    {
                                      new: "bg-blue-500",
                                      contacted: "bg-yellow-500",
                                      qualified: "bg-green-500",
                                      unqualified: "bg-red-500",
                                    } as Record<string, string>
                                  )[lead.status] || "bg-blue-500"
                                }`}
                              />
                              <img
                                src="/map-icon.svg"
                                alt={lead.name}
                                className="relative z-10 h-11 w-11 drop-shadow-md"
                              />
                            </div>
                          </AdvancedMarker>
                        ))}

                        {selectedMarker &&
                          (() => {
                            const lead = leadsWithCoords.find((l: any) => l._id === selectedMarker);
                            if (!lead) return null;
                            return (
                              <InfoWindow
                                position={{ lat: lead.coordinates.lat, lng: lead.coordinates.lng }}
                                onCloseClick={() => setSelectedMarker(null)}
                              >
                                <div className="max-w-xs p-2 text-gray-900">
                                  <div className="mb-1 flex items-start justify-between gap-2">
                                    <h3 className="text-sm font-bold text-gray-900 leading-snug">
                                      {lead.name}
                                    </h3>
                                    <span
                                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${
                                        (
                                          {
                                            new: "bg-blue-100 text-blue-700",
                                            contacted: "bg-yellow-100 text-yellow-700",
                                            qualified: "bg-green-100 text-green-700",
                                            unqualified: "bg-red-100 text-red-700",
                                          } as Record<string, string>
                                        )[lead.status] || "bg-blue-100 text-blue-700"
                                      }`}
                                    >
                                      {lead.status}
                                    </span>
                                  </div>

                                  {lead.accountType && (
                                    <div className="mb-2">
                                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                                        {formatAccountType(lead.accountType)}
                                      </span>
                                    </div>
                                  )}

                                  {lead.address && (
                                    <p className="mb-2 text-xs text-gray-600">
                                      {lead.address}
                                    </p>
                                  )}

                                  {lead.estimatedValue && (
                                    <p className="mb-2 text-xs font-semibold text-emerald-600">
                                      Est. Value: ${lead.estimatedValue.toLocaleString()}
                                    </p>
                                  )}

                                  <Button
                                    size="sm"
                                    className="mt-1 h-7 w-full text-xs font-medium"
                                    onClick={() => navigate(`/leads/${lead._id}`)}
                                  >
                                    View Details
                                  </Button>
                                </div>
                              </InfoWindow>
                            );
                          })()}
                      </GoogleMap>
                    </>
                  );
                })()}
              </div>
            )}
          </Card>
        </div>
        <CreateLeadSheet
          isOpen={isCreateSheetOpen}
          onClose={handleCloseCreateSheet}
          onAdd={handleCreateLead}
        />
      </DashboardLayout>
    </APIProvider>
  );
}
